import React, { useEffect } from "react";
import { parse } from "node-html-parser";
import { GetServerSideProps } from "next";
import Head from "next/head";
import axios from "axios";
import nookies from "nookies";
import style from "./Preview.module.scss";
import { ENDPOINTS } from "@/api/routing";
import { concatObject, customizeStateObject } from "@/helper/helper";
import { IPreview } from "@/types/all.types";
import dynamic from "next/dynamic";
import { findPreviewComponentForList } from "@/components/page-modules/builder/__shared/helper/find-preview";
import { IQrType } from "@/types/qr.types";
import PreviewWelcomeScreen from "@/components/common/PreviewWelcomeScreen/PreviewWelcomeScreen";
import { useSolutionsSelect } from "@/states/solution.state";
import { QR_TYPES } from "@/constant/qr_type.enum";
import cn from "classnames";
const RenderTypes = ({
  data,
  slug,
  headContent
}: IPreview<Record<string, any>>) => {
  const {
    setSolutionsType
  } = useSolutionsSelect();
  const PreviewType = dynamic<IQrType>(() => findPreviewComponentForList(data.data.type), {
    ssr: false
  });
  useEffect(() => {
    if (typeof data !== "string") {
      setSolutionsType({
        solutionSelectedType: data.data.type
      });
      customizeStateObject[data.data.type].setData(data.data.payload);
    }
  }, []);
  if (typeof data === "string") {
    return <>
        <Head>
          {headContent?.map((element, index) => {
          // Handling the title tag separately
          if (element.tag === "title") {
            return <title key={index}>{element.content}</title>;
          }
          if (element.tag === "style") {
            return <style key={index}>{element.content}</style>;
          }
          // Handling meta, link, and other self-closing tags
          return React.createElement(element.tag, {
            key: index,
            ...element.attributes
          });
        })}
        </Head>
        <div dangerouslySetInnerHTML={{
        __html: data
      }} className={style.preview} />
      </>;
  }
  return <div className={cn(style.container, "qrTypesPreview")} data-sentry-component="RenderTypes" data-sentry-source-file="[slug].tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="[slug].tsx">
        <title>
          {data.data.type.charAt(0).toUpperCase() + data.data.type.substring(1, data.data.type.length)}
        </title>
        <meta name="robots" content="noindex,nofollow" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
      </Head>
      {data?.data?.type !== QR_TYPES.TEXT && <PreviewWelcomeScreen data={data.data} />}
      <PreviewType data={data?.data} slug={slug} data-sentry-element="PreviewType" data-sentry-source-file="[slug].tsx" />
    </div>;
};
export default RenderTypes;
export const getServerSideProps: GetServerSideProps = async ctx => {
  const headers = ctx.req.headers;
  const {
    slug,
    lang
  } = ctx.query;
  const {
    x_scanner_device_id
  } = nookies.get(ctx);
  const device = x_scanner_device_id ? {
    ["x-scanner-device-id"]: x_scanner_device_id
  } : undefined;
  const headerWithLang = {
    ...device,
    "x-lang": lang as string || "en"
  };
  const {
    JSON_DATA
  } = concatObject({
    initialData: headers
  });
  try {
    const res = await axios.post<IPreview<Record<string, any>>["data"]>(ENDPOINTS.POST_QR_SCAN(slug as string), {
      metadata: JSON_DATA
    }, {
      headers: {
        ...headerWithLang
      }
    });
    const contentType = res.headers["content-type"];
    if (contentType.includes("text/html") && typeof res.data === "string") {
      const root = parse(res.data);
      const tags = root.querySelectorAll("head > *");
      const bodyContent = root.querySelector("body")?.innerHTML;
      const headContent: {
        tag: string;
        attributes: Record<string, string>;
        content: string;
      }[] = [];
      tags.forEach(tag => {
        headContent.push({
          tag: tag.rawTagName,
          attributes: tag.attributes,
          content: tag.textContent
        });
      });
      return {
        props: {
          JSON_DATA,
          slug,
          data: bodyContent,
          headContent
        }
      };
    }
    if (!contentType.includes("application/json")) {
      return {
        props: {
          JSON_DATA,
          slug,
          data: res.data
        }
      };
    }
    if (!customizeStateObject[res.data.data.type]) {
      return {
        notFound: true
      };
    } else if (!x_scanner_device_id) nookies.set(ctx, "x_scanner_device_id", res.headers["x-scanner-device-id"], {
      maxAge: 30 * 24 * 60 * 60,
      path: "/"
    });
    return {
      props: {
        JSON_DATA,
        slug,
        data: res.data
      }
    };
  } catch (error) {
    return {
      notFound: true
    };
  }
};